import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { invitationsApi } from '@/api/invitationsWrapper';
import type { InvitationResponse } from 'we-tree-api-client/v2';
import { useFamilyStore } from '@/hooks/useStores';
import { uiStore } from '@/stores/UIStore';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { getInvitationInviteeLabel } from '@/utils/invitationDisplay';

interface OverviewPendingInvitesCardProps {
  familyId: string;
  canManage: boolean;
  onViewAll: () => void;
  onSummaryLoaded?: (pendingCount: number) => void;
}

const isPendingInvite = (invitation: InvitationResponse): boolean => {
  if (invitation.status !== 'pending') return false;
  return new Date(invitation.expiresAt) >= new Date();
};

const OverviewPendingInvitesCard: React.FC<OverviewPendingInvitesCardProps> = observer(
  ({ familyId, canManage, onViewAll, onSummaryLoaded }) => {
    const { t } = useTranslation('overview');
    const familyStore = useFamilyStore();
    const [invitations, setInvitations] = useState<InvitationResponse[]>([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const loadInvitations = useCallback(async () => {
      if (!canManage) {
        onSummaryLoaded?.(0);
        return;
      }
      setIsLoading(true);
      try {
        const result = await invitationsApi.getInvitations(familyId);
        if (result.success) {
          const pending = result.data.invitations.filter(isPendingInvite);
          setPendingCount(pending.length);
          setInvitations(pending.slice(0, 5));
          onSummaryLoaded?.(pending.length);
        } else if (!(result.error?.isAuthError() && result.error?.status === 403)) {
          setPendingCount(0);
          setInvitations([]);
          onSummaryLoaded?.(0);
        }
      } catch {
        setPendingCount(0);
        setInvitations([]);
        onSummaryLoaded?.(0);
      } finally {
        setIsLoading(false);
      }
    }, [familyId, canManage, onSummaryLoaded]);

    useEffect(() => {
      loadInvitations();
    }, [loadInvitations]);

    const handleResend = async (invitationId: string) => {
      const result = await invitationsApi.resendInvitation(invitationId, {
        locale: uiStore.preferredLanguage
      });
      if (result.success) {
        loadInvitations();
      } else {
        alert(result.error?.getUserMessage() || t('pendingInvites.resendFailed'));
      }
    };

    const handleCancel = async (invitationId: string) => {
      if (!confirm(t('pendingInvites.cancelConfirm'))) return;
      const result = await invitationsApi.cancelInvitation(invitationId);
      if (result.success) {
        loadInvitations();
      } else {
        alert(result.error?.getUserMessage() || t('pendingInvites.cancelFailed'));
      }
    };

    if (!canManage) return null;

    if (isLoading) {
      return (
        <section className="overview-card" aria-labelledby="overview-pending-invites-title">
          <header className="overview-card__head">
            <h2 id="overview-pending-invites-title" className="overview-card__title">
              {t('pendingInvites.title')}
            </h2>
          </header>
          <p className="overview-card__empty">{t('pendingInvites.loading')}</p>
        </section>
      );
    }

    if (pendingCount <= 0) return null;

    return (
      <section className="overview-card" aria-labelledby="overview-pending-invites-title">
        <header className="overview-card__head">
          <h2 id="overview-pending-invites-title" className="overview-card__title">
            {t('pendingInvites.title')}
            <span className="overview-card__count">{pendingCount}</span>
          </h2>
          <button type="button" className="overview-card__more" onClick={onViewAll}>
            {t('pendingInvites.manage')}
          </button>
        </header>
        <div className="overview-card__body">
          {invitations.map((invitation) => {
            const inviteeLabel = getInvitationInviteeLabel(invitation, (id) => familyStore.getPerson(id)?.fullName);
            return (
              <div key={invitation.id} className="invite-item">
                <div>
                  <div className="invite-item__name">{inviteeLabel}</div>
                  <div className="invite-item__meta">
                    {t('pendingInvites.sent', { time: formatRelativeTime(invitation.createdAt) })}
                  </div>
                </div>
                <div className="invite-item__actions">
                  <button type="button" className="btn-text" onClick={() => handleResend(invitation.id)}>
                    {t('pendingInvites.resend')}
                  </button>
                  <button type="button" className="btn-text" onClick={() => handleCancel(invitation.id)}>
                    {t('pendingInvites.cancel')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }
);

export default OverviewPendingInvitesCard;
